import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../api/admin.api.js";
import { uploadImages } from "../../api/upload.api.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Input } from "../../components/ui/Input.jsx";

/**
 * Bulk-import floor plan images. Drop in N images, and we upload them in
 * one round-trip then create one Floor Plan row per image, titled from
 * the file name.
 *
 * Page-based so admins have room to review previews and progress
 * feedback isn't crammed into a dialog.
 */
export default function BulkImportFloorPlansPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [titlePrefix, setTitlePrefix] = useState("");
  const [files, setFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // string[] (object URLs)
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    document.title = "Bulk import floor plans · Admin";
  }, []);

  // Cleanup any leftover preview blob URLs when the component unmounts —
  // otherwise navigating away mid-import leaks them.
  useEffect(() => {
    return () => {
      previews.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePicked = (selected) => {
    const arr = Array.from(selected ?? []);
    if (arr.length === 0) return;
    setFiles((prev) => [...prev, ...arr]);
    setPreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAt = (i) => {
    setPreviews((prev) => {
      const url = prev[i];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, idx) => idx !== i);
    });
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const titleFromFilename = (file) => {
    const base = file.name.replace(/\.[^/.]+$/, "");
    const cleaned = base
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const words = cleaned
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    return titlePrefix ? `${titlePrefix.trim()} — ${words}` : words || "Untitled floor plan";
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error("Add at least one image.");
      return;
    }

    setBusy(true);
    try {
      // 1. Upload all images in a single multipart request.
      setProgress({ stage: "Uploading images", done: 0, total: files.length });
      const uploaded = await uploadImages(files, "floor-plans");
      if (uploaded.length !== files.length) {
        toast.warning(`${uploaded.length} of ${files.length} images uploaded.`);
      }
      setProgress({ stage: "Creating floor plans", done: 0, total: uploaded.length });

      // 2. Create one floor plan per uploaded path. Sequential so a single
      //    failure surfaces clearly instead of being lost in Promise.all.
      let made = 0;
      const errors = [];
      for (let i = 0; i < uploaded.length; i++) {
        const u = uploaded[i];
        const sourceFile = files[i] ?? files[0];
        const title = titleFromFilename(sourceFile);
        try {
          await adminApi.create("floor-plans", {
            title,
            image: u.url,
            sortOrder: 0,
          });
          made += 1;
        } catch (err) {
          errors.push(err?.response?.data?.message ?? err.message);
        }
        setProgress({ stage: "Creating floor plans", done: i + 1, total: uploaded.length });
      }

      if (errors.length === 0) {
        toast.success(`Imported ${made} floor plan${made === 1 ? "" : "s"}.`);
      } else {
        toast.error(`Imported ${made} of ${uploaded.length}. Some failed: ${errors[0]}`);
      }

      previews.forEach((u) => URL.revokeObjectURL(u));
      queryClient.invalidateQueries({ queryKey: ["floor-plans"] });
      navigate("/floor-plans");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Bulk import failed");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <FormShell
      title="Bulk import floor plans"
      backTo="/floor-plans"
      backLabel="Back to floor plans"
      onSubmit={(e) => {
        e?.preventDefault?.();
        handleSubmit();
      }}
      primaryAction={
        <Button onClick={handleSubmit} loading={busy}>
          Import{" "}
          {files.length > 0 ? `${files.length} floor plan${files.length === 1 ? "" : "s"}` : ""}
        </Button>
      }
      secondaryAction={
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate("/floor-plans")}
          disabled={busy}
        >
          Cancel
        </Button>
      }
      sidebar={
        <FormSection title="How this works">
          <p className="text-sm text-text-secondary">
            Optionally set a title prefix, then drop or pick multiple images.
          </p>
          <p className="mt-3 text-sm text-text-secondary">
            One Floor Plan row will be created per image, titled from the file
            name (snake-case &amp; dashes converted to Title Case). Run more
            than once to keep adding to the gallery.
          </p>
          {progress && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/10 p-3 text-xs text-accent">
              <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
              <span>
                {progress.stage} — {progress.done} / {progress.total}
              </span>
            </div>
          )}
        </FormSection>
      }
    >
      <FormSection title="Target">
        <Input
          label="Title prefix (optional)"
          value={titlePrefix}
          onChange={(e) => setTitlePrefix(e.target.value)}
          hint='e.g. "Hampton" — prepended to each title generated from the filename.'
          disabled={busy}
        />
      </FormSection>

      <FormSection
        title="Images"
        description="Pick multiple — one Floor Plan row will be created per image."
      >
        <div className="rounded-xl border border-dashed border-border bg-bg-elevated-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-text-secondary">
              {files.length === 0
                ? "No images yet. Click 'Pick images' to start."
                : `${files.length} image${files.length === 1 ? "" : "s"} ready to import.`}
            </p>
            <Button
              type="button"
              variant="secondary"
              leftIcon={<ImagePlus className="h-4 w-4" />}
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              Pick images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => handlePicked(e.target.files)}
            />
          </div>

          {previews.length > 0 && (
            <ul className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {previews.map((src, i) => (
                <li
                  key={`${src}-${i}`}
                  className="relative aspect-square overflow-hidden rounded-md border border-border-subtle bg-bg-elevated"
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    disabled={busy}
                    className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-bg-base/85 text-text-primary hover:bg-danger hover:text-white disabled:opacity-50"
                    aria-label={`Remove ${files[i]?.name ?? "image"}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <p className="absolute inset-x-0 bottom-0 truncate bg-bg-base/85 px-1 py-0.5 text-[10px] text-text-secondary">
                    {files[i]?.name}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </FormSection>
    </FormShell>
  );
}
